import React from "react";
import {
  StyleSheet,
  TouchableOpacity,
} from "react-native";
import { Text, View } from "../components/Themed";
import { useQuery, gql, useSubscription } from "@apollo/client";
import TableOrderView from "../components/TableOrderView";
import * as Themed from "../components/Themed";

const ThemedText = Themed.Text;

function getPrefixedTableCode(tableNumber, tableCode) {
  const number = tableNumber == null ? "" : String(tableNumber).trim();
  const code = tableCode == null ? "" : String(tableCode).trim().toUpperCase();
  if (!number || !code) return "";
  return code.startsWith(number) ? code : `${number}${code}`;
}

function getRouteTableTokens(params = {}) {
  if (params.tableToken) return [String(params.tableToken).trim().toUpperCase()].filter(Boolean);
  const rawCode = params.tableCode == null ? "" : String(params.tableCode).trim().toUpperCase();
  const prefixedCode = getPrefixedTableCode(params.tableNumber, rawCode);
  return [rawCode, prefixedCode].filter((value, index, values) => value && values.indexOf(value) === index);
}

const TABLE_DETAILS = gql`
  query getTableDetails($token: String!) {
    products(token: $token) {
      id
      title
      imageUrls
      prices
      capabilities
      category
      variants
      volumes
      allergens
      options {
        id
        title
        type
        required
        options {
          id
          title
          prices
        }
      }
    }
    tables(token: $token) {
      id
      operator
      available
      published
      capacity
      finalized
      number
      token
      codes {
        code
        permanent
        enabled
        label
      }
      location
      x
      y
      orders {
        id
        operator
        productIds
        capabilities
        quantities
        tableId
        timestamp
        status
      }
    }
    categories(token: $token) {
      id
      name
      order
    }
    locations(token: $token) {
      id
      name
      myposEnabled
    }
    tableByToken(token: $token) {
      id
      operator
      available
      published
      capacity
      finalized
      number
      token
      codes {
        code
        permanent
        enabled
        label
      }
      location
      x
      y
      orders {
        id
        operator
        productIds
        quantities
        capabilities
        variants
        tableId
        timestamp
        status
      }
    }
  }
`;

const TABLE_SUBSCRIPTION = gql`
  subscription TableSubscription($token: String!, $tableId: ID!) {
    table(token: $token, id: $tableId) {
      id
      operator
      available
      published
      capacity
      finalized
      number
      token
      codes {
        code
        permanent
        enabled
        label
      }
      location
      x
      y
      orders {
        id
        operator
        productIds
        quantities
        tableId
        variants
        timestamp
        status
      }
    }
  }
`;

export default function TableDetailsScreen(props) {
  const routeParams = props.route.params || {};
  const tokenCandidates = React.useMemo(
    () => getRouteTableTokens(routeParams),
    [routeParams.tableToken, routeParams.tableNumber, routeParams.tableCode],
  );
  const [tokenCandidateIndex, setTokenCandidateIndex] = React.useState(0);
  const token = tokenCandidates[tokenCandidateIndex] || "";

  React.useEffect(() => {
    setTokenCandidateIndex(0);
  }, [tokenCandidates.join("|")]);

  const { loading, error, data } = useQuery(TABLE_DETAILS, {
    variables: { token },
    skip: !token,
    nextFetchPolicy: 'cache-only',
  });

  const resolvedTable = data?.tableByToken;
  const tableId = resolvedTable?.id || routeParams.tableId;
  const hasTokenFallback = tokenCandidateIndex + 1 < tokenCandidates.length;

  React.useEffect(() => {
    if (!loading && !resolvedTable && hasTokenFallback) {
      setTokenCandidateIndex((index) => index + 1);
    }
  }, [loading, resolvedTable, hasTokenFallback]);

  const ret = useSubscription(TABLE_SUBSCRIPTION, {
    variables: { token, tableId },
    skip: !token || !tableId,
  });

  if (!token) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>wasted</Text>
        <TouchableOpacity
          onPress={() => props.navigation.replace("Menu")}
          style={styles.link}
        >
          <Text style={styles.linkText}>Please, ask staff for help! | Click for main menu</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (loading) return <ThemedText>Loading...</ThemedText>;

  if (!data || (!resolvedTable && hasTokenFallback)) return <ThemedText>Loading...</ThemedText>;

  if (!resolvedTable)
    return (
      <View style={styles.container}>
        <Text style={styles.title}>wasted</Text>
        <TouchableOpacity
          onPress={() => props.navigation.replace("Menu")}
          style={styles.link}
        >
          <Text style={styles.linkText}>Please, ask staff for help! | Click for main menu</Text>
        </TouchableOpacity>
      </View>
    );

  let table = resolvedTable;
  if (ret.data?.table) {
    table = { ...table, ...ret.data.table };
  }

  // Get the location for this table to check myposEnabled
  const location = data.locations?.find(l => l.id === table.location);
  const tableRoute = {
    ...props.route,
    params: {
      ...routeParams,
      tableId: table.id,
      tableToken: token,
    },
  };

  return <TableOrderView {...props} route={tableRoute} products={data.products} table={table} categories={data.categories} tables={data.tables || []} location={location}/>;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    fontFamily: "Pricedown-Bl",
  },
  link: {
    marginTop: 15,
    paddingVertical: 15,
  },
  linkText: {
    fontSize: 14,
    fontFamily: "Pricedown-Bl",
    color: "#2e78b7",
  },
});
